﻿import { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Button, Form, Input, Modal, Select, Spin, Tag, message } from 'antd';
import { mFetch, MApiError } from '../mApi';
import { MPageTitle } from '../MobileApp';

interface Task {
  id: string;
  taskType: string;
  inputKeyword?: string | null;
  status: string;
  result?: { title?: string; summary?: string } | null;
  publishTargetType?: string | null;
  publishTargetId?: string | null;
  error?: string | null;
  createdAt?: string;
}

const STATUS: Record<string, { text: string; color: string }> = {
  pending: { text: '待生成', color: 'default' },
  running: { text: '生成中', color: 'processing' },
  success: { text: '已生成', color: 'blue' },
  failed: { text: '失败', color: 'red' },
  approved: { text: '已审核', color: 'gold' },
  published: { text: '已发布官网', color: 'green' },
  rejected: { text: '已驳回', color: 'default' },
};

export default function MArticles() {
  const [items, setItems] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [creating, setCreating] = useState(false);
  const [busyId, setBusyId] = useState('');
  const [form] = Form.useForm();
  const pollRef = useRef<number | null>(null);

  const load = useCallback(async () => {
    setErr('');
    try {
      const r = await mFetch<{ items: Task[] }>('/admin/content/tasks?limit=30');
      setItems(r.items || []);
    } catch (e) {
      setErr(e instanceof MApiError ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    return () => {
      if (pollRef.current) window.clearInterval(pollRef.current);
    };
  }, [load]);

  const pollTask = (id: string, done: (t: Task) => void) => {
    if (pollRef.current) window.clearInterval(pollRef.current);
    const started = Date.now();
    pollRef.current = window.setInterval(async () => {
      try {
        const t = await mFetch<Task>(`/admin/content/tasks/${id}`);
        if (['success', 'failed', 'rejected'].includes(t.status) || Date.now() - started > 120_000) {
          if (pollRef.current) window.clearInterval(pollRef.current);
          done(t);
          load();
        }
      } catch {
        /* keep polling */
      }
    }, 3000);
  };

  const createAndGenerate = async () => {
    const v = await form.validateFields();
    setCreating(false);
    try {
      const task = await mFetch<Task>('/admin/content/tasks', {
        method: 'POST',
        body: JSON.stringify({ taskType: 'article', inputKeyword: v.keyword, industry: v.industry || undefined }),
      });
      await mFetch(`/admin/content/tasks/${task.id}/generate`, { method: 'POST', body: '{}' });
      message.success('已开始生成，请稍候…');
      load();
      pollTask(task.id, (t) => {
        if (t.status === 'success') message.success(`生成完成：${t.result?.title || ''}`);
        else message.warning(`生成结束：${t.status}`);
      });
    } catch (e) {
      message.error(e instanceof MApiError ? e.message : String(e));
    }
  };

  const publishSite = async (t: Task) => {
    setBusyId(t.id);
    try {
      await mFetch(`/admin/content/tasks/${t.id}/approve`, { method: 'POST', body: '{}' });
      await mFetch(`/admin/content/tasks/${t.id}/publish`, { method: 'POST', body: '{}' });
      message.success('已发布到官网');
      load();
    } catch (e) {
      message.error(e instanceof MApiError ? e.message : String(e));
    } finally {
      setBusyId('');
    }
  };

  const publishWechat = async (t: Task) => {
    if (!t.publishTargetId) {
      message.warning('请先发布到官网（生成公众号草稿需官网文章）');
      return;
    }
    setBusyId(t.id);
    try {
      const d = await mFetch<{ mediaId: string; mock: boolean }>('/wechat-mp/drafts', {
        method: 'POST',
        body: JSON.stringify({ articleId: t.publishTargetId }),
      });
      const p = await mFetch<{ publishId: string; mock: boolean }>(`/wechat-mp/drafts/${d.mediaId}/publish`, {
        method: 'POST',
        body: '{}',
      });
      message.success(`已提交公众号发布${p.mock ? '（模拟模式）' : ''}：${p.publishId}`);
    } catch (e) {
      if (e instanceof MApiError && e.hint) message.error(`${e.message}｜${e.hint}`, 6);
      else message.error(e instanceof MApiError ? e.message : String(e));
    } finally {
      setBusyId('');
    }
  };

  return (
    <div>
      <MPageTitle title="文章" />
      <div style={{ padding: '0 16px 8px' }}>
        <Button type="primary" block data-testid="m-article-create" onClick={() => setCreating(true)}>
          ＋ AI 新建文章
        </Button>
      </div>
      {loading ? (
        <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
      ) : err ? (
        <div style={{ padding: 16 }}><Alert type="warning" showIcon title={err} /></div>
      ) : (
        <div style={{ padding: '0 16px' }}>
          {items.length === 0 && <div style={{ color: '#8a8aa3', padding: 20, textAlign: 'center' }}>暂无任务</div>}
          {items.map((t) => {
            const st = STATUS[t.status] || { text: t.status, color: 'default' };
            const busy = busyId === t.id;
            return (
              <div key={t.id} data-testid="m-article-item" style={{ background: '#141428', border: '1px solid #26264a', borderRadius: 10, padding: 12, marginBottom: 8 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontWeight: 600, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {t.result?.title || t.inputKeyword || '未命名'}
                  </span>
                  <Tag color={st.color}>{st.text}</Tag>
                </div>
                {t.error ? <div style={{ fontSize: 12, color: '#ff7875', marginTop: 4 }}>{t.error}</div> : null}
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  {t.status === 'success' && (
                    <Button size="small" type="primary" loading={busy} onClick={() => publishSite(t)} data-testid="m-article-publish-site">
                      发布官网
                    </Button>
                  )}
                  {t.status === 'published' && t.publishTargetType === 'blog' && (
                    <Button size="small" loading={busy} onClick={() => publishWechat(t)} data-testid="m-article-publish-wechat">
                      发公众号
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
      <Modal
        open={creating}
        title="AI 新建文章"
        okText="生成"
        onOk={createAndGenerate}
        onCancel={() => setCreating(false)}
        styles={{ body: { background: '#141428' } }}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="keyword" label="主题 / 关键词" rules={[{ required: true, message: '请输入主题' }]}>
            <Input placeholder="如：数字孪生工厂解决方案" data-testid="m-article-keyword" />
          </Form.Item>
          <Form.Item name="industry" label="行业（可选）">
            <Select
              allowClear
              options={['制造业', '水务', '能源', '园区', '通用'].map((v) => ({ value: v, label: v }))}
              placeholder="选择行业"
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
